import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Loader2, Sparkles, Mail, Check, Plus, ArrowLeft, ArrowUp, Pin, PinOff, Trash2, Pencil, ShieldQuestion, BellRing } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Only recruiters (all tiers) — NOT Super Admin (JADM).
const ALLOWED_ROLES = ['RADM', 'RECR', 'CADM', 'CREC', 'client_admin', 'client_recruiter'];

/**
 * JobiAvatar — Jobasti rounded-square mascot.
 *
 * A soft rounded-square body in Jobasti blue (Emergent-Neo style). When
 * `active` is true (hover or notifications present), stick arms, stick legs
 * and eyes "grow out" from the body with a springy scale animation. When
 * inactive the character shows only the clean square badge.
 *
 * @param {number}  size       overall width in px (height scales to match)
 * @param {boolean} active     reveal eyes/arms/legs (default true so inline
 *                             chat avatars always show the character face)
 * @param {boolean} showLimbs  render stick arms + legs (only the floating
 *                             mascot uses this — inline avatars stay simple)
 * @param {boolean} blink      collapse eyes to short lines for a blink frame
 * @param {boolean} wave       tilt/wiggle the whole avatar (notification wave)
 */
const JobiAvatar = ({
  size = 40,
  active = true,
  showLimbs = false,
  blink = false,
  wave = false,
  className = '',
}) => {
  const viewBoxW = 48;
  const viewBoxH = showLimbs ? 62 : 48;
  const width = size;
  const height = Math.round(size * (viewBoxH / viewBoxW));
  // Feature reveal easing — slight overshoot so limbs pop out
  const springy = 'cubic-bezier(0.34,1.56,0.64,1)';
  const featureStyle = (delay = 0) => ({
    transformBox: 'fill-box',
    transformOrigin: 'center',
    transform: active ? 'scale(1)' : 'scale(0)',
    opacity: active ? 1 : 0,
    transition: `transform 0.32s ${springy} ${delay}ms, opacity 0.22s ease ${delay}ms`,
  });
  const limbStyle = (origin, delay = 0) => ({
    transformBox: 'fill-box',
    transformOrigin: origin,
    transform: active ? 'scale(1)' : 'scale(0.02)',
    opacity: active ? 1 : 0,
    transition: `transform 0.4s ${springy} ${delay}ms, opacity 0.22s ease ${delay}ms`,
  });

  return (
    <div
      className={`relative inline-flex items-center justify-center select-none ${className}`}
      style={{
        width,
        height,
        transformOrigin: '50% 80%',
        animation: wave ? 'jobi-tilt 1.5s ease-in-out' : undefined,
        filter: showLimbs ? 'drop-shadow(0 3px 6px rgba(29,78,216,0.25))' : undefined,
      }}
      aria-label="Jobi"
    >
      <svg
        viewBox={`-2 0 ${viewBoxW + 4} ${viewBoxH}`}
        width="100%"
        height="100%"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id="jobiSquareGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#60A5FA" />
            <stop offset="55%" stopColor="#3B82F6" />
            <stop offset="100%" stopColor="#1D4ED8" />
          </linearGradient>
        </defs>

        {/* Arms — render *behind* the body so they read as coming out of it */}
        {showLimbs && (
          <>
            {/* Left arm — grows out from the left shoulder */}
            <g style={limbStyle('100% 0%', 0)}>
              <line
                x1="-4" y1="34" x2="6" y2="26"
                stroke="#1D4ED8" strokeWidth="2.4" strokeLinecap="round"
              />
              <circle cx="-4" cy="34" r="2.2" fill="#1D4ED8" />
            </g>
            {/* Right arm */}
            <g style={limbStyle('0% 0%', 20)}>
              <line
                x1="42" y1="26" x2="52" y2="34"
                stroke="#1D4ED8" strokeWidth="2.4" strokeLinecap="round"
              />
              <circle cx="52" cy="34" r="2.2" fill="#1D4ED8" />
            </g>
          </>
        )}

        {/* Body — rounded-square badge */}
        <rect
          x="6" y="6" width="36" height="36" rx="10" ry="10"
          fill="url(#jobiSquareGrad)"
          stroke="#1E3A8A"
          strokeWidth="0.8"
        />
        {/* Soft top-inner highlight — gives the square a subtle glossy depth */}
        <rect
          x="9" y="9" width="30" height="14" rx="7" ry="7"
          fill="rgba(255,255,255,0.14)"
        />

        {/* Eyes — pop into view when active */}
        <g style={featureStyle(30)}>
          {blink ? (
            <>
              <rect x="15" y="23" width="6" height="1.8" rx="0.9" fill="#0F172A" />
              <rect x="27" y="23" width="6" height="1.8" rx="0.9" fill="#0F172A" />
            </>
          ) : (
            <>
              <circle cx="18" cy="24" r="2.6" fill="#0F172A" />
              <circle cx="30" cy="24" r="2.6" fill="#0F172A" />
              <circle cx="19" cy="23.1" r="0.85" fill="#ffffff" />
              <circle cx="31" cy="23.1" r="0.85" fill="#ffffff" />
            </>
          )}
        </g>

        {/* Legs */}
        {showLimbs && (
          <>
            <g style={limbStyle('50% 0%', 40)}>
              <line
                x1="18" y1="42" x2="16" y2="55"
                stroke="#1D4ED8" strokeWidth="2.6" strokeLinecap="round"
              />
              <ellipse cx="14.4" cy="56.4" rx="3" ry="1.6" fill="#1D4ED8" />
            </g>
            <g style={limbStyle('50% 0%', 70)}>
              <line
                x1="30" y1="42" x2="32" y2="55"
                stroke="#1D4ED8" strokeWidth="2.6" strokeLinecap="round"
              />
              <ellipse cx="33.6" cy="56.4" rx="3" ry="1.6" fill="#1D4ED8" />
            </g>
          </>
        )}
      </svg>
    </div>
  );
};

/**
 * Routes where Jobi must NEVER render — public/anonymous surfaces.
 * Even if a recruiter is logged in and navigates to one of these URLs
 * (e.g. previewing their own job posting), Jobi must stay hidden.
 */
const isPublicRoute = (pathname) => {
  if (!pathname) return false;
  if (pathname === '/' || pathname === '/for-companies') return true;
  const publicPrefixes = [
    '/login', '/register', '/onboarding',
    '/forgot-password', '/reset-password', '/prospect-register',
    '/unauthorized', '/async-interview',
  ];
  if (publicPrefixes.some(p => pathname === p || pathname.startsWith(p + '/'))) return true;
  // Company-slug based public pages: /:slug/jobs, /:slug/jobs/:id, /:slug/jobs/:id/apply, /:slug/about
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length >= 2 && (segments[1] === 'jobs' || segments[1] === 'about')) return true;
  return false;
};

// Relative time — "now" / "2h" / "3d" / date
function timeAgo(iso) {
  if (!iso) return '';
  const s = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 7 * 86400) return `${Math.floor(s / 86400)}d`;
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' });
}

const JobiChatbox = () => {
  const { userProfile } = useAuth();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const publicRoute = isPublicRoute(location.pathname);

  const SUGGESTIONS = [
    { label: t('jobi.suggestions.dailyBriefing'), action: 'chat' },
    { label: t('jobi.suggestions.draftOutreach'), action: 'chat' },
    { label: t('jobi.suggestions.silentThreeDays'), action: 'chat' },
    { label: t('jobi.suggestions.weeklyDigest'), action: 'weekly_digest' },
    { label: t('jobi.suggestions.rejectionTemplate'), action: 'chat' },
  ];
  const [isOpen, setIsOpen] = useState(false);
  // view: 'list' | 'convo'
  const [view, setView] = useState('list');
  const [threads, setThreads] = useState([]);
  const [threadPreviews, setThreadPreviews] = useState({}); // { [threadId]: lastMessagePreview }
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [insights, setInsights] = useState({ waitingCandidates: [], todayStats: null });
  const [mascotHover, setMascotHover] = useState(false);
  const [waveTick, setWaveTick] = useState(false);
  const [sendingAck, setSendingAck] = useState(false);
  const [ackResult, setAckResult] = useState(null);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);
  const [blinkNow, setBlinkNow] = useState(false);
  const [reminders, setReminders] = useState([]);
  const [memories, setMemories] = useState([]);
  // IDs (waiting applicationIds + due reminderIds) that the user has already
  // seen once. Persisted per-user so the red badge doesn't nag on refresh.
  const seenStorageKey = userProfile?.id ? `jobi_notif_seen_v1_${userProfile.id}` : null;
  const [seenIds, setSeenIds] = useState(() => {
    try {
      if (!seenStorageKey) return new Set();
      const raw = localStorage.getItem(seenStorageKey);
      return new Set(raw ? JSON.parse(raw) : []);
    } catch { return new Set(); }
  });
  const persistSeen = (s) => {
    if (!seenStorageKey) return;
    try { localStorage.setItem(seenStorageKey, JSON.stringify(Array.from(s))); } catch { /* quota */ }
  };
  const toastedRemindersRef = useRef(new Set());
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const roleOk = userProfile && ALLOWED_ROLES.includes(userProfile.role);
  const userId = userProfile?.id;
  const orgId = userProfile?.organization_id || userProfile?.organizationId || '';

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(() => { scrollToBottom(); }, [messages]);
  useEffect(() => { if (isOpen && view === 'convo') inputRef.current?.focus(); }, [isOpen, view, activeThreadId]);

  // ── Insights ──────────────────────────────────────────────
  const fetchInsights = useCallback(async () => {
    if (!roleOk) return;
    try {
      const url = `${API}/api/jobi/insights${orgId ? `?organization_id=${orgId}` : ''}`;
      const r = await axios.get(url);
      if (r.data?.success) setInsights(r.data.data);
    } catch { /* silent */ }
  }, [roleOk, orgId]);
  useEffect(() => {
    if (!roleOk) return;
    fetchInsights();
    const id = setInterval(fetchInsights, 60_000);
    return () => clearInterval(id);
  }, [roleOk, fetchInsights]);

  // Blink loop: toggle `blinkNow` for ~140ms every ~3.5s (with jitter) so the
  // JobiAvatar SVG collapses its eyes into a natural blink frame.
  useEffect(() => {
    let cancelled = false;
    const loop = async () => {
      while (!cancelled) {
        const wait = 3000 + Math.random() * 2000;
        await new Promise(r => setTimeout(r, wait));
        if (cancelled) break;
        setBlinkNow(true);
        await new Promise(r => setTimeout(r, 140));
        if (cancelled) break;
        setBlinkNow(false);
      }
    };
    loop();
    return () => { cancelled = true; };
  }, []);

  // ── Wave loop ─────────────────────────────────────────────
  useEffect(() => {
    if (!insights.waitingCandidates?.length) { setWaveTick(false); return; }
    let cancelled = false;
    const loop = async () => {
      while (!cancelled) {
        setWaveTick(true);
        await new Promise(r => setTimeout(r, 1500));
        if (cancelled) break;
        setWaveTick(false);
        await new Promise(r => setTimeout(r, 8500));
      }
    };
    loop();
    return () => { cancelled = true; };
  }, [insights.waitingCandidates?.length]);

  // ── Threads ───────────────────────────────────────────────
  const loadThreads = useCallback(async () => {
    if (!roleOk || !userId) return;
    try {
      const r = await axios.get(`${API}/api/jobi/threads`, { params: { user_id: userId } });
      if (r.data?.success) setThreads(r.data.data || []);
    } catch { /* silent */ }
  }, [roleOk, userId]);
  useEffect(() => { if (isOpen && roleOk) loadThreads(); }, [isOpen, roleOk, loadThreads]);

  // Fetch last-message preview for each thread when we don't have one yet
  const fetchPreviewsFor = useCallback(async (list) => {
    if (!userId) return;
    for (const t of list) {
      if (threadPreviews[t.id]) continue;
      try {
        const r = await axios.get(`${API}/api/jobi/threads/${t.id}/messages`, { params: { user_id: userId } });
        const msgs = r.data?.data?.messages || [];
        const last = msgs[msgs.length - 1];
        if (last) setThreadPreviews(prev => ({ ...prev, [t.id]: last.content.slice(0, 90) }));
      } catch { /* silent */ }
    }
  }, [userId]);
  useEffect(() => { if (isOpen && threads.length) fetchPreviewsFor(threads); }, [isOpen, threads, fetchPreviewsFor]);

  const openThread = async (id) => {
    setActiveThreadId(id);
    setView('convo');
    setMessages([]);
    try {
      const r = await axios.get(`${API}/api/jobi/threads/${id}/messages`, { params: { user_id: userId } });
      if (r.data?.success) {
        const msgs = (r.data.data.messages || []).map(m => {
          const raw = m.content || '';
          const isBot = m.role === 'assistant';
          return {
            id: m.id,
            role: isBot ? 'bot' : 'user',
            text: isBot ? stripActions(raw) : raw,
            suggestions: isBot ? parseSuggestions(raw) : undefined,
          };
        });
        setMessages(msgs);
      }
    } catch { /* silent */ }
  };

  const startNewThread = () => {
    setActiveThreadId(null);
    setMessages([]);
    setView('convo');
  };

  const createThread = async (title) => {
    try {
      const r = await axios.post(`${API}/api/jobi/threads`, { user_id: userId, organization_id: orgId || null, title: title || 'New chat' });
      if (r.data?.success) {
        setThreads(prev => [r.data.data, ...prev]);
        setActiveThreadId(r.data.data.id);
        return r.data.data.id;
      }
    } catch { /* silent */ }
    return null;
  };

  const deleteThread = async (id) => {
    if (!window.confirm('Delete this chat? It will be archived and hidden.')) return;
    try {
      await axios.delete(`${API}/api/jobi/threads/${id}`, { params: { user_id: userId } });
      setThreads(prev => prev.filter(t => t.id !== id));
      if (activeThreadId === id) { setActiveThreadId(null); setMessages([]); setView('list'); }
    } catch { /* silent */ }
    setOpenMenuId(null);
  };

  const togglePin = async (t) => {
    try {
      const r = await axios.patch(`${API}/api/jobi/threads/${t.id}`, { user_id: userId, is_pinned: !t.is_pinned });
      if (r.data?.success) setThreads(prev => prev.map(x => x.id === t.id ? r.data.data : x)
        .sort((a, b) => (b.is_pinned - a.is_pinned) || (new Date(b.updated_at) - new Date(a.updated_at))));
    } catch { /* silent */ }
    setOpenMenuId(null);
  };

  const renameThread = async (id, title) => {
    try {
      const r = await axios.patch(`${API}/api/jobi/threads/${id}`, { user_id: userId, title });
      if (r.data?.success) setThreads(prev => prev.map(t => t.id === id ? r.data.data : t));
    } catch { /* silent */ }
    setRenamingId(null);
  };

  // ── Reminders (per-user) ─────────────────────────────────
  const fetchReminders = useCallback(async () => {
    if (!roleOk || !userId) return;
    try {
      const r = await axios.get(`${API}/api/jobi/reminders`, { params: { user_id: userId } });
      if (r.data?.success) setReminders(r.data.data || []);
    } catch { /* silent */ }
  }, [roleOk, userId]);
  useEffect(() => {
    fetchReminders();
    const id = setInterval(fetchReminders, 60_000);
    return () => clearInterval(id);
  }, [fetchReminders]);

  // ── Memory (durable per-user facts Jobi remembers) ───────
  const fetchMemory = useCallback(async () => {
    if (!roleOk || !userId) return;
    try {
      const r = await axios.get(`${API}/api/jobi/memory`, { params: { user_id: userId } });
      if (r.data?.success) setMemories(r.data.data || []);
    } catch { /* silent */ }
  }, [roleOk, userId]);
  useEffect(() => { fetchMemory(); }, [fetchMemory]);

  const markReminderDone = useCallback(async (id) => {
    try {
      const r = await axios.patch(`${API}/api/jobi/reminders/${id}`, { user_id: userId, status: 'done' });
      if (r.data?.success) { toast.success(t('jobi.reminders.completed')); fetchReminders(); }
    } catch { toast.error('Could not update reminder'); }
  }, [userId, fetchReminders, t]);

  // Toast once per session when a reminder comes due while the app is open
  useEffect(() => {
    const nowMs = Date.now();
    for (const r of reminders) {
      if (r.status !== 'open' || !r.remind_at) continue;
      if (new Date(r.remind_at).getTime() > nowMs) continue;
      if (toastedRemindersRef.current.has(r.id)) continue;
      toastedRemindersRef.current.add(r.id);
      toast(r.description, {
        duration: 8000,
        icon: <BellRing className="w-4 h-4 text-amber-500" />,
        action: { label: t('jobi.reminders.markDone'), onClick: () => markReminderDone(r.id) },
      });
    }
  }, [reminders, markReminderDone, t]);

  // ── Actions ──────────────────────────────────────────────
  // Execute a single confirmed/instant action. Returns a status message string.
  const executeAction = useCallback(async (a) => {
    try {
      if (a.name === 'set_reminder' && a.args?.description && a.args?.remind_at) {
        const r = await axios.post(`${API}/api/jobi/reminders`, {
          user_id: userId, organization_id: orgId || null,
          description: a.args.description, remind_at: a.args.remind_at,
        });
        if (r.data?.success) {
          fetchReminders();
          return `✅ Reminder saved: "${a.args.description}" on ${new Date(a.args.remind_at).toLocaleString()}`;
        }
        return `⚠️ Could not save reminder.`;
      }
      if (a.name === 'mark_reminder_done' && a.args?.reminder_id) {
        const r = await axios.patch(`${API}/api/jobi/reminders/${a.args.reminder_id}`, { user_id: userId, status: 'done' });
        if (r.data?.success) { fetchReminders(); return '✅ Reminder marked done.'; }
        return '⚠️ Could not update reminder.';
      }
      if (a.name === 'send_ack_email' && Array.isArray(a.args?.application_ids)) {
        const r = await axios.post(`${API}/api/jobi/send-ack-email`, { application_ids: a.args.application_ids, recruiter_id: userId });
        if (r.data?.success) {
          fetchInsights();
          return `✅ Sent ${r.data.data.sent} acknowledgement email(s).`;
        }
        return `⚠️ Send failed: ${r.data?.message || 'unknown'}`;
      }
      if (a.name === 'move_candidate_stage' && a.args?.application_id && a.args?.status) {
        const r = await axios.put(`${API}/api/applications/${a.args.application_id}/status`, { status: a.args.status });
        if (r.data?.success !== false) {
          fetchInsights();
          return `✅ Moved candidate to "${a.args.status}".`;
        }
        return `⚠️ Could not move candidate: ${r.data?.message || 'unknown error'}`;
      }
      if (a.name === 'send_custom_email' && a.args?.application_id && a.args?.subject && a.args?.body) {
        const r = await axios.post(`${API}/api/jobi/send-custom-email`, {
          application_id: a.args.application_id, subject: a.args.subject, body: a.args.body,
          email_type: a.args.email_type || 'custom', recruiter_id: userId,
        });
        if (r.data?.success) {
          fetchInsights();
          return `✅ Email sent to ${a.args.candidate_name || r.data.data?.to || 'candidate'}.`;
        }
        return `⚠️ Send failed: ${r.data?.message || 'unknown'}`;
      }
      if (a.name === 'add_candidate_note' && a.args?.application_id && a.args?.note) {
        const r = await axios.post(`${API}/api/jobi/add-note`, { application_id: a.args.application_id, note: a.args.note, recruiter_id: userId });
        if (r.data?.success) return `✅ Note added${a.args.candidate_name ? ` to ${a.args.candidate_name}` : ''}.`;
        return '⚠️ Could not add note.';
      }
      if (a.name === 'save_memory' && a.args?.content) {
        const r = await axios.post(`${API}/api/jobi/memory`, {
          user_id: userId, organization_id: orgId || null,
          category: a.args.category || 'general', content: a.args.content,
        });
        if (r.data?.success) {
          fetchMemory();
          toast.success('Jobi will remember that', { duration: 3000 });
          return `✅ Saved to memory: "${a.args.content}"`;
        }
        return `⚠️ ${r.data?.message || 'Could not save memory.'}`;
      }
      return '';
    } catch (e) {
      return `⚠️ ${e?.response?.data?.message || e.message || 'action failed'}`;
    }
  }, [userId, orgId, fetchInsights, fetchReminders]);

  // Parse INSTANT <action> tags emitted by Jobi and execute them client-side (no confirmation).
  const parseAndExecuteInstantActions = async (replyText) => {
    const rx = /<action\s+name="([^"]+)">([\s\S]*?)<\/action>/g;
    let m; const actions = [];
    while ((m = rx.exec(replyText)) !== null) {
      try { actions.push({ name: m[1], args: JSON.parse(m[2]) }); } catch { /* ignore malformed */ }
    }
    for (const a of actions) {
      const status = await executeAction(a);
      if (status) {
        setMessages(prev => [...prev, { id: `act_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`, role: 'bot', text: status }]);
      }
    }
  };

  // Parse CONFIRM-GATED <action-plan> tags — return array attached to the message so the UI
  // can render a Confirm / Cancel card. Nothing is executed here.
  const parseActionPlans = (text) => {
    const rx = /<action-plan\s+name="([^"]+)">([\s\S]*?)<\/action-plan>/g;
    const plans = []; let m;
    while ((m = rx.exec(text)) !== null) {
      try {
        const args = JSON.parse(m[2]);
        plans.push({
          id: `plan_${Date.now()}_${plans.length}_${Math.random().toString(36).slice(2, 6)}`,
          name: m[1],
          args,
          status: 'pending', // 'pending' | 'running' | 'done' | 'cancelled' | 'error'
          resultText: '',
        });
      } catch { /* ignore malformed */ }
    }
    return plans;
  };

  // Confirm-run a pending action-plan attached to a message.
  const confirmActionPlan = async (msgId, planId) => {
    // Snapshot the target plan from the CURRENT messages state before we mutate anything.
    let target = null;
    setMessages(prev => prev.map(m => {
      if (m.id !== msgId) return m;
      return {
        ...m,
        actionPlans: (m.actionPlans || []).map(p => {
          if (p.id === planId) { target = p; return { ...p, status: 'running' }; }
          return p;
        }),
      };
    }));
    // Give React a tick to apply the state before firing the async call
    await new Promise(r => setTimeout(r, 0));
    if (!target) return;
    const status = await executeAction({ name: target.name, args: target.args });
    const isOk = status.startsWith('✅');

    // Short, prominent toast so the user gets instant success/failure feedback
    // even if the chat has scrolled past the action card.
    const stripped = status.replace(/^✅\s*/, '').replace(/^⚠️\s*/, '');
    if (isOk) {
      toast.success(stripped || 'Done', { duration: 3500 });
    } else if (status) {
      toast.error(stripped || 'Action failed', { duration: 4500 });
    }

    setMessages(prev => prev.map(m => m.id !== msgId ? m : {
      ...m,
      actionPlans: (m.actionPlans || []).map(p => p.id === planId ? {
        ...p,
        status: isOk ? 'done' : 'error',
        resultText: status,
      } : p),
    }));
  };

  const cancelActionPlan = (msgId, planId) => {
    setMessages(prev => prev.map(m => m.id !== msgId ? m : {
      ...m,
      actionPlans: (m.actionPlans || []).map(p => p.id === planId ? { ...p, status: 'cancelled' } : p),
    }));
    toast('Action cancelled', { duration: 2500 });
  };

  // Strip <action>, <action-plan> AND <suggestions> tags from visible text (final, complete-tag pass)
  const stripActions = (text) => (text || '')
    .replace(/<action\s+name="[^"]+">[\s\S]*?<\/action>/g, '')
    .replace(/<action-plan\s+name="[^"]+">[\s\S]*?<\/action-plan>/g, '')
    .replace(/<suggestions>[\s\S]*?<\/suggestions>/g, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();

  // Display-time sanitizer: also hides *partial* tags mid-stream so users never see
  // "<action name=..." (or even a lone "<") bleeding into the reply while Claude is still typing.
  const sanitizeForDisplay = (text) => {
    if (!text) return '';
    let out = stripActions(text);
    // 1) Cut anything from the first unclosed <action|action-plan|suggestions> opener onward.
    const openerIdx = out.search(/<(action(?:-plan)?|suggestions)\b/i);
    if (openerIdx !== -1) {
      const rest = out.slice(openerIdx);
      if (!/<\/(action-plan|action|suggestions)>/i.test(rest)) {
        out = out.slice(0, openerIdx);
      }
    }
    // 2) Cut any *trailing* partial tag prefix that hasn't produced a closing '>' yet
    //    (e.g. "<", "<s", "<sug", "<action", "<action-plan"). Only when it's at the very
    //    tail of the string, so we don't destroy legitimate inline "<" usage mid-sentence.
    out = out.replace(/<[a-zA-Z][a-zA-Z0-9-]*$/g, '').replace(/<$/g, '');
    return out.replace(/\s+$/, '');
  };

  // Render assistant markdown-lite: **bold** and *italic* → real tags.
  // Everything else (line breaks, bullets, "•", numbered lists) already
  // renders correctly via `whitespace-pre-wrap`. Keeps zero deps.
  const renderRich = (text) => {
    if (!text) return '';
    const parts = [];
    // Match **bold** first, then remaining *italic*.
    const BOLD_RE = /\*\*([^*\n][^*]*?)\*\*/g;
    let lastIdx = 0;
    let m;
    let key = 0;
    while ((m = BOLD_RE.exec(text)) !== null) {
      if (m.index > lastIdx) parts.push(text.slice(lastIdx, m.index));
      parts.push(<strong key={`b${key++}`} className="font-semibold text-white">{m[1]}</strong>);
      lastIdx = m.index + m[0].length;
    }
    if (lastIdx < text.length) parts.push(text.slice(lastIdx));

    // Second pass on plain string parts: single *italic*.
    const ITAL_RE = /(^|\s)\*([^*\n][^*]*?)\*(?=\s|$|[.,;:!?)])/g;
    const out = [];
    parts.forEach((p, i) => {
      if (typeof p !== 'string') { out.push(p); return; }
      let idx = 0; let mm;
      while ((mm = ITAL_RE.exec(p)) !== null) {
        if (mm.index + mm[1].length > idx) out.push(p.slice(idx, mm.index + mm[1].length));
        out.push(<em key={`i${i}-${key++}`} className="italic text-slate-100">{mm[2]}</em>);
        idx = mm.index + mm[0].length;
      }
      if (idx < p.length) out.push(p.slice(idx));
    });
    return out;
  };

  // Parse <suggestions>[...]</suggestions> — returns array of chip strings
  const parseSuggestions = (text) => {
    const m = /<suggestions>\s*(\[[\s\S]*?\])\s*<\/suggestions>/.exec(text || '');
    if (!m) return [];
    try {
      const arr = JSON.parse(m[1]);
      if (Array.isArray(arr)) return arr.filter(s => typeof s === 'string' && s.length < 80).slice(0, 4);
    } catch { /* ignore */ }
    return [];
  };

  // ── Send message ──────────────────────────────────────────
  const sendMessage = async (userText) => {
    if (!userText.trim() || isStreaming || !userId) return;
    const text = userText.trim();
    setInputValue('');
    if (view !== 'convo') setView('convo');

    let tid = activeThreadId;
    let isFirst = false;
    if (!tid) { tid = await createThread(text.slice(0, 40)); isFirst = true; if (!tid) return; }
    else { isFirst = messages.length === 0; }

    const userMsg = { id: `u_${Date.now()}`, role: 'user', text };
    const botMsg = { id: `b_${Date.now() + 1}`, role: 'bot', text: '', streaming: true };
    setMessages(prev => [...prev, userMsg, botMsg]);
    setIsStreaming(true);

    const context = {
      user: {
        name: [userProfile?.first_name, userProfile?.last_name].filter(Boolean).join(' ') || userProfile?.email || 'there',
        role: userProfile?.role || 'recruiter',
        org_name: userProfile?.organization_name || userProfile?.organizationName || '',
        timezone: (typeof Intl !== 'undefined' && Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC',
        now_iso: new Date().toISOString(),
        now_local: new Date().toLocaleString(),
        preferred_language: (i18n?.resolvedLanguage || i18n?.language || 'en').split('-')[0],
      },
      insights,
      reminders: (reminders || []).slice(0, 20).map(r => ({ id: r.id, description: r.description, remind_at: r.remind_at, related_application_id: r.related_application_id })),
      memory: (memories || []).slice(0, 30).map(m => ({ id: m.id, category: m.category, content: m.content })),
    };

    if (isFirst) {
      axios.post(`${API}/api/jobi/title`, { message: text })
        .then(r => {
          const t = r.data?.title;
          if (t) axios.patch(`${API}/api/jobi/threads/${tid}`, { user_id: userId, title: t })
            .then(() => setThreads(prev => prev.map(x => x.id === tid ? { ...x, title: t } : x)));
        }).catch(() => {});
    }

    try {
      const resp = await fetch(`${API}/api/jobi/chat`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ thread_id: tid, user_id: userId, message: text, context }),
      });
      if (!resp.ok || !resp.body) throw new Error(`HTTP ${resp.status}`);
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6).trim();
          if (!payload) continue;
          let obj;
          try { obj = JSON.parse(payload); } catch { continue; }
          if (obj.delta) setMessages(prev => prev.map(m => m.id === botMsg.id ? { ...m, text: (m.text || '') + obj.delta } : m));
          else if (obj.error) setMessages(prev => prev.map(m => m.id === botMsg.id ? { ...m, text: `⚠️ ${obj.error}`, streaming: false } : m));
          else if (obj.done) {
            setMessages(prev => prev.map(m => {
              if (m.id !== botMsg.id) return m;
              const raw = m.text || '';
              parseAndExecuteInstantActions(raw);
              const actionPlans = parseActionPlans(raw);
              return {
                ...m,
                text: stripActions(raw),
                suggestions: parseSuggestions(raw),
                actionPlans,
                streaming: false,
              };
            }));
          }
        }
      }
    } catch (e) {
      setMessages(prev => prev.map(m => m.id === botMsg.id ? { ...m, text: `⚠️ ${e.message || 'Something went wrong'}`, streaming: false } : m));
    } finally {
      setIsStreaming(false);
      loadThreads();
    }
  };

  // ── Waving-badge quick open ──────────────────────────────
  // NOTE: We deliberately do NOT auto-send the "great matches waiting" prompt
  // here. Auto-firing created a fresh thread every single login and cluttered
  // the Messages list. Instead we just open the panel; the user then picks
  // "Draft outreach to waiting" from the suggestion chips if they want it.
  const openWithWaitingPrompt = () => {
    setIsOpen(true);
    setView('list');
    // Also mark as seen — deferred so the freshly-computed dueReminders/waiting
    // set is what gets snapshotted.
    setTimeout(() => markNotificationsSeen(), 0);
  };

  // Compose the waiting-candidates outreach draft prompt on demand
  // (called from a suggestion chip inside the panel, never auto-fired).
  const draftWaitingOutreach = () => {
    if (!insights.waitingCandidates?.length) return;
    const names = insights.waitingCandidates.slice(0, 3).map(c => `${c.candidateName} (${c.matchScore}%)`).join(', ');
    const count = insights.waitingCandidates.length;
    const q = `I have ${count} great match${count === 1 ? '' : 'es'} waiting on me: ${names}. Draft a warm, professional outreach email I can send, plus one tailored subject line each.`;
    setView('convo');
    setActiveThreadId(null);
    setMessages([]);
    setTimeout(() => sendMessage(q), 200);
  };

  // ── Ack + digest (unchanged behaviour) ───────────────────
  const sendAckToWaiting = async () => {
    if (sendingAck || !insights.waitingCandidates?.length) return;
    setSendingAck(true);
    setAckResult(null);
    try {
      const ids = insights.waitingCandidates.map(c => c.applicationId).filter(Boolean);
      const r = await axios.post(`${API}/api/jobi/send-ack-email`, { application_ids: ids, recruiter_id: userId || null });
      if (r.data?.success) {
        setAckResult(r.data.data);
        setMessages(prev => [...prev, {
          id: `sys_${Date.now()}`, role: 'bot',
          text: `Sent ${r.data.data.sent} acknowledgement email${r.data.data.sent === 1 ? '' : 's'} 🎉${r.data.data.failed?.length > 0 ? ` (${r.data.data.failed.length} failed).` : '.'} The badge should clear on the next refresh.`,
        }]);
        setTimeout(fetchInsights, 800);
      } else setAckResult({ sent: 0, failed: [{ error: r.data?.message || 'send failed' }] });
    } catch (e) {
      setAckResult({ sent: 0, failed: [{ error: e?.response?.data?.message || e.message || 'send failed' }] });
    } finally { setSendingAck(false); }
  };

  useEffect(() => {
    if (!roleOk || !userId) return;
    const key = `jobi_weekly_last_sent_${userId}`;
    const last = parseInt(localStorage.getItem(key) || '0', 10);
    const now = Date.now();
    const isMonday = new Date().getDay() === 1;
    const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
    if (!isMonday || last > sevenDaysAgo) return;
    localStorage.setItem(key, String(now));
    axios.post(`${API}/api/jobi/weekly-digest/send`, { recruiter_id: userId, organization_id: orgId }).catch(() => {});
  }, [roleOk, userId, orgId]);

  const sendWeeklyDigest = async () => {
    if (!userId) return;
    setView('convo');
    setMessages(prev => [...prev, { id: `sys_${Date.now()}`, role: 'bot', text: 'Sending your weekly digest to your inbox…' }]);
    try {
      const r = await axios.post(`${API}/api/jobi/weekly-digest/send`, { recruiter_id: userId, organization_id: orgId });
      if (r.data?.success) {
        const d = r.data.data;
        setMessages(prev => [...prev, {
          id: `sys_${Date.now() + 1}`, role: 'bot',
          text: `Delivered to ${d.sent_to} 📬 — ${d.applications} applications, ${d.goodMatches} good matches, ${d.waiting} waiting > 48 h. Check your inbox for the full breakdown.`,
        }]);
      } else setMessages(prev => [...prev, { id: `sys_${Date.now() + 1}`, role: 'bot', text: `⚠️ ${r.data?.message || 'Digest failed'}` }]);
    } catch (e) {
      setMessages(prev => [...prev, { id: `sys_${Date.now() + 1}`, role: 'bot', text: `⚠️ ${e?.response?.data?.message || e.message}` }]);
    }
  };

  if (!roleOk || publicRoute) return null;

  const waitingCount = insights.waitingCandidates?.length || 0;
  const openReminders = (reminders || []).filter(r => r.status === 'open');
  const dueReminders = openReminders.filter(r => r.remind_at && new Date(r.remind_at).getTime() <= Date.now());
  // Badge only counts *unseen* items — clears the moment the panel opens
  const unseenWaiting = (insights.waitingCandidates || []).filter(c => !seenIds.has(`w:${c.applicationId}`));
  const unseenDue = dueReminders.filter(r => !seenIds.has(`r:${r.id}`));
  const notifCount = unseenWaiting.length + unseenDue.length;
  const activeThread = threads.find(t => t.id === activeThreadId);

  // Mark all currently-surfaced items as seen (called when the panel is opened).
  const markNotificationsSeen = () => {
    const next = new Set(seenIds);
    (insights.waitingCandidates || []).forEach(c => c.applicationId && next.add(`w:${c.applicationId}`));
    dueReminders.forEach(r => r.id && next.add(`r:${r.id}`));
    // Cap to last 500 IDs so localStorage doesn't grow unbounded
    if (next.size > 500) {
      const arr = Array.from(next).slice(-500);
      setSeenIds(new Set(arr));
      persistSeen(new Set(arr));
    } else {
      setSeenIds(next);
      persistSeen(next);
    }
  };
  const openPanel = () => { setIsOpen(true); markNotificationsSeen(); };

  return (
    <>
      {/* Floating launcher — "Ask Jobi" pill in Jobasti blue */}
      {!isOpen && (
        <button
          type="button"
          onClick={() => { setMascotHover(false); (notifCount > 0 ? openWithWaitingPrompt : openPanel)(); }}
          onMouseEnter={() => setMascotHover(true)}
          onMouseLeave={() => setMascotHover(false)}
          className="fixed bottom-6 right-6 z-[60] focus:outline-none group"
          aria-label={notifCount > 0 ? `Open Ask Jobi — ${notifCount} notifications` : 'Open Ask Jobi assistant'}
          data-testid="jobi-mascot-button"
        >
          <div
            className="relative flex items-center gap-2 rounded-full pl-3 pr-4 py-3 text-white transition-transform duration-300 ease-out will-change-transform group-hover:-translate-y-0.5"
            style={{
              background: 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)',
              boxShadow: '0 10px 24px -6px rgba(29,78,216,0.5), inset 0 1px 0 rgba(255,255,255,0.25)',
            }}
          >
            {/* Speech bubble with two little eyes — subtle character in the launcher */}
            <span className="relative flex items-center justify-center w-6 h-6">
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path
                  d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v8A2.5 2.5 0 0 1 17.5 16H11l-4 3.5V16H6.5A2.5 2.5 0 0 1 4 13.5v-8Z"
                  fill="white"
                />
                <circle cx="9.5" cy="9.5" r="1.2" fill="#1D4ED8" />
                <circle cx="14.5" cy="9.5" r="1.2" fill="#1D4ED8" />
              </svg>
            </span>
            <span className="text-sm font-semibold pr-0.5">Ask Jobi</span>
            {notifCount > 0 && (
              <span
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[10px] font-bold shadow-lg shadow-red-500/40 ring-2 ring-white animate-[jobi-pulse_1.6s_ease-in-out_infinite]"
                data-testid="jobi-notification-badge"
              >{notifCount > 9 ? '9+' : notifCount}</span>
            )}
          </div>
        </button>
      )}

      {/* Chat panel — Emergent-Messages style, dark */}
      {isOpen && (
        <div
          className="fixed bottom-6 right-6 z-[60] w-[380px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-4rem)] flex flex-col bg-slate-900 text-slate-100 rounded-2xl shadow-[0_20px_60px_-10px_rgba(0,0,0,0.6)] border border-slate-800 overflow-hidden"
          data-testid="jobi-chat-panel"
          onClick={() => setOpenMenuId(null)}
        >
          {/* Header */}
          <div className="flex items-center gap-2 px-4 pt-4 pb-3 border-b border-slate-800">
            {view === 'convo' ? (
              <button type="button" onClick={() => { setView('list'); setActiveThreadId(null); setMessages([]); }} className="p-1 -ml-1 rounded-md hover:bg-white/10 transition-colors" aria-label="Back" data-testid="jobi-back-btn">
                <ArrowLeft className="w-4 h-4 text-slate-300" />
              </button>
            ) : (
              <JobiAvatar size={24} />
            )}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-[15px] text-white truncate">
                {view === 'convo' ? (activeThread?.title || t('jobi.newChat')) : t('jobi.header')}
              </div>
              {view === 'convo' && (
                <div className="text-[11px] text-slate-400 truncate">{t('jobi.privateNote')}</div>
              )}
            </div>
            {view === 'list' && (
              <button type="button" onClick={startNewThread} className="p-1.5 rounded-full hover:bg-white/10 transition-colors" aria-label={t('jobi.newChat')} title={t('jobi.newChat')} data-testid="jobi-new-thread-btn">
                <Plus className="w-4 h-4 text-slate-300" />
              </button>
            )}
            <button type="button" onClick={() => setIsOpen(false)} className="p-1.5 rounded-full hover:bg-white/10 transition-colors" aria-label="Close" data-testid="jobi-close-btn">
              <X className="w-4 h-4 text-slate-300" />
            </button>
          </div>

          {/* Body */}
          {view === 'list' ? (
            <div className="flex-1 overflow-y-auto py-1.5 divide-y divide-slate-800/60" data-testid="jobi-thread-list">
              {/* Waiting-candidates call-to-action — only surfaces when it's actionable */}
              {waitingCount > 0 && (
                <div className="mx-3 mt-2 mb-1 rounded-xl bg-gradient-to-br from-blue-600/25 to-blue-700/10 border border-blue-500/40 px-3 py-2.5" data-testid="jobi-waiting-cta">
                  <div className="flex items-start gap-2.5">
                    <div className="mt-0.5 w-6 h-6 rounded-md bg-blue-500/25 border border-blue-400/40 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-3.5 h-3.5 text-blue-200" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] font-semibold text-white leading-tight">
                        {waitingCount} great match{waitingCount === 1 ? '' : 'es'} waiting on you
                      </div>
                      <div className="text-[11px] text-slate-300 mt-0.5 truncate">
                        {insights.waitingCandidates.slice(0, 2).map(c => c.candidateName).join(', ')}
                        {insights.waitingCandidates.length > 2 ? ` +${insights.waitingCandidates.length - 2}` : ''}
                      </div>
                      <button
                        type="button"
                        onClick={draftWaitingOutreach}
                        className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-semibold text-blue-100 hover:text-white bg-blue-500/25 hover:bg-blue-500/40 border border-blue-400/40 rounded-md px-2 py-1 transition-colors"
                        data-testid="jobi-draft-outreach-btn"
                      >
                        <Sparkles className="w-3 h-3" /> Draft outreach
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {/* My Reminders — due ones highlighted, check off inline */}
              {openReminders.length > 0 && (
                <div className="mx-3 mt-2 mb-1 rounded-xl bg-slate-800/50 border border-slate-700/60 px-3 py-2.5" data-testid="jobi-reminders-card">
                  <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-400 font-semibold">
                    <BellRing className={`w-3 h-3 ${dueReminders.length > 0 ? 'text-amber-400' : ''}`} />
                    {t('jobi.reminders.title')}
                    {dueReminders.length > 0 && (
                      <span className="ml-auto text-[10px] font-bold text-amber-400 normal-case tracking-normal">{dueReminders.length} {t('jobi.reminders.due').toLowerCase()}</span>
                    )}
                  </div>
                  <div className="mt-2 space-y-2">
                    {openReminders.slice(0, 4).map(r => {
                      const isDue = r.remind_at && new Date(r.remind_at).getTime() <= Date.now();
                      return (
                        <div key={r.id} className="flex items-start gap-2" data-testid={`jobi-reminder-row-${r.id}`}>
                          <button
                            type="button"
                            onClick={() => markReminderDone(r.id)}
                            className="group mt-0.5 w-4 h-4 rounded border border-slate-500 hover:border-emerald-400 hover:bg-emerald-500/20 flex items-center justify-center flex-shrink-0 transition-colors"
                            aria-label={t('jobi.reminders.markDone')}
                            title={t('jobi.reminders.markDone')}
                            data-testid={`jobi-reminder-done-${r.id}`}
                          >
                            <Check className="w-3 h-3 text-transparent group-hover:text-emerald-400 transition-colors" />
                          </button>
                          <div className="flex-1 min-w-0">
                            <div className="text-[12px] text-slate-200 leading-snug">{r.description}</div>
                            <div className={`text-[10.5px] mt-0.5 ${isDue ? 'text-amber-400 font-semibold' : 'text-slate-400'}`}>
                              {isDue ? `${t('jobi.reminders.due')} · ` : ''}
                              {r.remind_at ? new Date(r.remind_at).toLocaleString(undefined, { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    {openReminders.length > 4 && (
                      <div className="text-[10.5px] text-slate-500">+{openReminders.length - 4} more</div>
                    )}
                  </div>
                </div>
              )}
              {threads.length === 0 && (
                <div className="p-4 text-center text-slate-400 text-sm">
                  <JobiAvatar size={48} className="mx-auto mb-3 opacity-95" />
                  Say hi to Jobi — start your first chat below.
                </div>
              )}
              {threads.map(thr => (
                <div
                  key={thr.id}
                  onClick={() => openThread(thr.id)}
                  className={`group flex items-start gap-3 px-4 py-3 cursor-pointer transition-colors ${activeThreadId === thr.id ? 'bg-slate-800/60' : 'hover:bg-slate-800/40'}`}
                  data-testid={`jobi-thread-${thr.id}`}
                >
                  <JobiAvatar size={32} className="flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      {renamingId === thr.id ? (
                        <input
                          autoFocus value={renameValue} onClick={e => e.stopPropagation()}
                          onChange={e => setRenameValue(e.target.value)}
                          onBlur={() => renameThread(thr.id, renameValue)}
                          onKeyDown={e => { if (e.key === 'Enter') renameThread(thr.id, renameValue); if (e.key === 'Escape') setRenamingId(null); }}
                          className="flex-1 text-[13px] font-semibold bg-slate-950 border border-slate-700 rounded px-1.5 py-0.5 text-white"
                        />
                      ) : (
                        <div className="text-[14px] font-semibold text-white truncate flex-1">
                          {thr.is_pinned && <Pin className="w-3 h-3 inline mr-1 -mt-0.5 text-slate-400" />}
                          {thr.title}
                        </div>
                      )}
                      <div className="text-[11px] text-slate-400 flex-shrink-0">{timeAgo(thr.updated_at)}</div>
                    </div>
                    <div className="text-[12px] text-slate-400 line-clamp-1 mt-0.5">
                      {threadPreviews[thr.id] || t('jobi.tapToOpen')}
                    </div>
                  </div>
                  {/* Row action menu */}
                  <div className="relative flex-shrink-0" onClick={e => e.stopPropagation()}>
                    <button
                      onClick={() => setOpenMenuId(openMenuId === thr.id ? null : thr.id)}
                      className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-white transition-opacity text-lg leading-none px-1.5 pb-0.5"
                      aria-label="Thread actions"
                    >⋯</button>
                    {openMenuId === thr.id && (
                      <div className="absolute right-0 top-6 z-10 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1 min-w-[130px] text-[12px]">
                        <button onClick={() => togglePin(thr)} className="w-full text-left px-3 py-1.5 text-slate-200 hover:bg-slate-700 flex items-center gap-2">
                          {thr.is_pinned ? <><PinOff className="w-3 h-3" /> Unpin</> : <><Pin className="w-3 h-3" /> Pin</>}
                        </button>
                        <button onClick={() => { setRenamingId(thr.id); setRenameValue(thr.title); setOpenMenuId(null); }} className="w-full text-left px-3 py-1.5 text-slate-200 hover:bg-slate-700 flex items-center gap-2">
                          <Pencil className="w-3 h-3" /> Rename
                        </button>
                        <button onClick={() => deleteThread(thr.id)} className="w-full text-left px-3 py-1.5 text-red-300 hover:bg-red-500/20 flex items-center gap-2">
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3" data-testid="jobi-convo">
              {messages.length === 0 ? (
                <div className="pt-2">
                  <div className="flex flex-col items-center text-center py-4">
                    <JobiAvatar size={52} className="mb-3" />
                    <div className="text-[15px] font-semibold text-white">{t('jobi.greeting', { name: userProfile?.first_name || 'there' })}</div>
                    <div className="text-[12px] text-slate-400 mt-1 max-w-[280px]">{t('jobi.empty')}</div>
                  </div>
                  <div className="space-y-2 mt-2" data-testid="jobi-suggestions">
                    {SUGGESTIONS.map((s, i) => (
                      <button
                        key={i} type="button"
                        onClick={() => s.action === 'weekly_digest' ? sendWeeklyDigest() : sendMessage(s.label)}
                        className="w-full text-left text-[12px] text-slate-200 bg-slate-800/60 hover:bg-slate-800 border border-slate-700/60 rounded-lg px-3 py-2 transition-colors flex items-start gap-2"
                        data-testid={`jobi-suggestion-${i}`}
                      >
                        <Sparkles className="w-3.5 h-3.5 mt-0.5 text-blue-400 flex-shrink-0" />
                        <span className="leading-snug">{s.label}</span>
                      </button>
                    ))}
                  </div>
                  {insights.todayStats && (insights.todayStats.applications > 0 || waitingCount > 0) && (
                    <div className="mt-4 bg-slate-800/60 border border-slate-700/60 rounded-lg px-3 py-2.5">
                      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold">{t('jobi.todaySoFar')}</div>
                      <div className="mt-1 text-[12px] text-slate-200 leading-snug">
                        <strong className="text-white">{insights.todayStats.applications}</strong> {t('jobi.applications')} ·{' '}
                        <span className="text-emerald-400 font-semibold">{insights.todayStats.goodMatches} {t('jobi.good')}</span>
                        {insights.todayStats.lowMatches > 0 ? <> · <span className="text-red-400">{insights.todayStats.lowMatches} {t('jobi.low')}</span></> : null}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                messages.map(m => (
                  <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className="flex flex-col max-w-[80%] gap-1.5">
                      {(m.text || m.streaming) && (
                        <div className={`rounded-2xl px-3.5 py-2 text-[13.5px] whitespace-pre-wrap leading-relaxed ${
                          m.role === 'user' ? 'bg-blue-600 text-white rounded-br-md self-end' : 'bg-slate-800 text-slate-100 rounded-bl-md'
                        }`}>
                          {m.role === 'user'
                            ? (m.text || '')
                            : (m.text
                                ? renderRich(sanitizeForDisplay(m.text))
                                : (m.streaming ? <span className="inline-flex items-center gap-1 text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> {t('jobi.thinking')}</span> : ''))}
                          {m.streaming && m.text ? <span className="inline-block w-1 h-3.5 ml-0.5 bg-slate-400 align-middle animate-pulse" /> : null}
                        </div>
                      )}
                      {/* Confirmation-gated action cards */}
                      {!m.streaming && m.actionPlans?.length > 0 && (
                        <div className="space-y-2" data-testid="jobi-action-plans">
                          {m.actionPlans.map(p => (
                            <div
                              key={p.id}
                              className="rounded-xl border border-slate-700 bg-slate-800/80 px-3 py-2.5 shadow-sm"
                              data-testid={`jobi-action-plan-${p.name}`}
                              data-plan-id={p.id}
                            >
                              <div className="flex items-start gap-2">
                                <ShieldQuestion className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                                <div className="flex-1 min-w-0">
                                  <div className="text-[11px] uppercase tracking-wider text-amber-400 font-semibold">{t('jobi.confirm.title')}</div>
                                  <div className="text-[13px] text-slate-100 leading-snug mt-0.5">
                                    {p.args?.summary || p.name}
                                  </div>
                                </div>
                              </div>
                              {p.name === 'send_custom_email' && p.args?.subject && (
                                <div className="mt-2 rounded-lg bg-slate-900/70 border border-slate-700/70 px-2.5 py-2 max-h-44 overflow-y-auto" data-testid="jobi-email-preview">
                                  <div className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-200">
                                    <Mail className="w-3 h-3 text-blue-300 flex-shrink-0" /> {p.args.subject}
                                  </div>
                                  <div className="text-[11px] text-slate-300 whitespace-pre-wrap mt-1.5 leading-relaxed">{p.args.body}</div>
                                </div>
                              )}
                              {p.name === 'add_candidate_note' && p.args?.note && (
                                <div className="mt-2 rounded-lg bg-slate-900/70 border border-slate-700/70 px-2.5 py-2" data-testid="jobi-note-preview">
                                  <div className="text-[11px] text-slate-300 whitespace-pre-wrap leading-relaxed">{p.args.note}</div>
                                </div>
                              )}
                              {p.status === 'pending' && (
                                <div className="flex items-center justify-end gap-2 mt-2.5">
                                  <button
                                    type="button"
                                    onClick={() => cancelActionPlan(m.id, p.id)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-red-500/10 text-red-300 hover:bg-red-500/20 border border-red-500/30 transition"
                                    data-testid={`jobi-action-cancel-${p.name}`}
                                  >
                                    <X className="w-3.5 h-3.5" />
                                    {t('jobi.confirm.cancel')}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => confirmActionPlan(m.id, p.id)}
                                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-emerald-500 text-white hover:bg-emerald-600 border border-emerald-500 transition"
                                    data-testid={`jobi-action-confirm-${p.name}`}
                                  >
                                    <Check className="w-3.5 h-3.5" />
                                    {t('jobi.confirm.confirm')}
                                  </button>
                                </div>
                              )}
                              {p.status === 'running' && (
                                <div className="flex items-center gap-1.5 mt-2 text-xs text-slate-300" data-testid={`jobi-action-running-${p.name}`}>
                                  <Loader2 className="w-3 h-3 animate-spin" /> {t('jobi.confirm.executing')}
                                </div>
                              )}
                              {p.status === 'done' && (
                                <div className="mt-2 text-xs text-emerald-400 flex items-center gap-1.5" data-testid={`jobi-action-done-${p.name}`}>
                                  <Check className="w-3.5 h-3.5" />
                                  {p.resultText || t('jobi.confirm.done')}
                                </div>
                              )}
                              {p.status === 'cancelled' && (
                                <div className="mt-2 text-xs text-slate-400" data-testid={`jobi-action-cancelled-${p.name}`}>
                                  {t('jobi.confirm.cancelled')}
                                </div>
                              )}
                              {p.status === 'error' && (
                                <div className="mt-2 text-xs text-red-300" data-testid={`jobi-action-error-${p.name}`}>
                                  {p.resultText}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                      {!m.streaming && m.suggestions?.length > 0 && (
                        <div className="grid grid-cols-2 gap-1.5 mt-1" data-testid="jobi-inline-suggestions">
                          {m.suggestions.map((s, i) => (
                            <button
                              key={i} type="button"
                              onClick={() => sendMessage(s)}
                              className="text-left text-[11.5px] leading-tight text-slate-200 bg-slate-800/70 hover:bg-blue-600 hover:text-white border border-slate-700 rounded-lg px-2.5 py-2 transition-colors"
                            >
                              {s}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>
          )}

          {/* Action bar */}
          {view === 'convo' && waitingCount > 0 && !isStreaming && (
            <div className="border-t border-slate-800 bg-slate-900 px-3 py-2">
              {ackResult ? (
                <div className="flex items-center gap-2 text-xs text-emerald-400" data-testid="jobi-ack-result">
                  <Check className="w-3.5 h-3.5" /> {ackResult.sent === 1 ? t('jobi.sentAck', { count: ackResult.sent }) : t('jobi.sentAckMany', { count: ackResult.sent })}
                  {ackResult.failed?.length > 0 ? ` · ${ackResult.failed.length} failed` : ''}
                </div>
              ) : (
                <button
                  type="button" onClick={sendAckToWaiting} disabled={sendingAck}
                  className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-blue-300 hover:text-blue-200 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg px-3 py-2 transition-colors disabled:opacity-60"
                  data-testid="jobi-send-ack-btn"
                >
                  {sendingAck ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5" />}
                  {sendingAck ? t('jobi.sending') : (waitingCount === 1 ? t('jobi.sendAckOne', { count: waitingCount }) : t('jobi.sendAckMany', { count: waitingCount }))}
                </button>
              )}
            </div>
          )}

          {/* Pill input — Emergent-style */}
          <div className="px-3 pb-3 pt-2 border-t border-slate-800/70">
            <div className="relative flex items-center bg-slate-800 rounded-full pl-4 pr-1 py-1 focus-within:ring-2 focus-within:ring-blue-500/60 transition-shadow">
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                onChange={e => setInputValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(inputValue); } }}
                placeholder={view === 'list' ? t('jobi.askAQuestion') : t('jobi.messageJobi')}
                onFocus={() => { if (view === 'list') setView('convo'); }}
                className="flex-1 bg-transparent border-none outline-none text-[13.5px] text-white placeholder-slate-400 py-1.5"
                data-testid="jobi-input"
                disabled={isStreaming}
              />
              <button
                type="button"
                onClick={() => sendMessage(inputValue)}
                disabled={!inputValue.trim() || isStreaming}
                className="w-9 h-9 flex-shrink-0 flex items-center justify-center bg-blue-500 hover:bg-blue-600 disabled:bg-slate-700 disabled:cursor-not-allowed text-white rounded-full transition-colors"
                aria-label="Send message"
                data-testid="jobi-send-btn"
              >
                {isStreaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowUp className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes jobi-breathe { 0%,100% { transform: translateY(0) scale(1); } 50% { transform: translateY(-3px) scale(1.01); } }
        @keyframes jobi-wiggle { 0% { transform: rotate(0) translateY(0); } 20% { transform: rotate(-6deg) translateY(-4px); } 40% { transform: rotate(8deg) translateY(-4px); } 60% { transform: rotate(-6deg) translateY(-4px); } 80% { transform: rotate(6deg) translateY(-2px); } 100% { transform: rotate(0) translateY(0); } }
        @keyframes jobi-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(239,68,68,0.6); } 50% { box-shadow: 0 0 0 10px rgba(239,68,68,0); } }
        /* Playful tilt to acknowledge waiting candidates */
        @keyframes jobi-tilt {
          0%,100% { transform: rotate(0deg); }
          20%     { transform: rotate(-9deg); }
          45%     { transform: rotate(7deg); }
          65%     { transform: rotate(-5deg); }
          85%     { transform: rotate(3deg); }
        }
        /* Blink — brief vertical squish reads as a natural eye-blink */
        @keyframes jobi-blink {
          0%, 92%, 100% { transform: scaleY(1); }
          95%, 97%      { transform: scaleY(0.1); }
        }
      `}</style>
    </>
  );
};

export default JobiChatbox;
